import React, { useEffect, useRef, useState } from "react";
import { FloppyDisk, TextAa, Globe, ArrowsClockwise, ImageSquare, Trash } from "@phosphor-icons/react";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const FONT_OPTIONS = ["Outfit", "Manrope", "Inter", "Bebas Neue", "Roboto", "Arial", "Helvetica", "Times New Roman", "Courier New", "Georgia", "system-ui"];

function TextField({ label, value, onChange, multiline, testId, placeholder }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <div>
      <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">{label}</label>
      <Tag
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 resize-y"
        data-testid={testId}
      />
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded border border-ink-600 bg-transparent cursor-pointer" data-testid={testId} />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-24 px-2 py-1 rounded bg-ink-900 border border-ink-600 text-white text-xs font-mono" />
      </div>
    </div>
  );
}

export default function SiteContentManager() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/site-content");
      setContent(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post("/admin/site-content/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_path", data.logo_path);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    try {
      await api.put("/admin/site-content", { logo_path: "" });
      set("logo_path", "");
      toast.success("Logo cleared — wordmark restored");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-content", content);
      setContent(data);
      toast.success("Site content saved — live on next page load");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (loading || !content) return <div className="text-zinc-500 text-sm" data-testid="site-content-loading">Loading site content…</div>;

  return (
    <section className="space-y-6" data-testid="site-content-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-1 inline-flex items-center gap-2">
            <TextAa size={14} weight="fill" /> Site Content & Typography
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Edit every word and font on the site.</h2>
          <p className="text-sm text-zinc-500 mt-1">All changes go live the moment you save. Reload the public site to see them.</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan text-xs inline-flex items-center gap-1.5"><ArrowsClockwise size={12} /> Reload</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 space-y-4 lg:col-span-2" data-testid="sc-branding-box">
          <h3 className="text-sm font-bold tracking-tight text-white inline-flex items-center gap-2"><ImageSquare size={14} /> Brand identity</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Site name (wordmark)" value={content.site_name} onChange={(v) => set("site_name", v)} testId="sc-site-name" placeholder="UNDAGROUND SHOOW" />
            <TextField label="Short name (mobile / share)" value={content.site_name_short} onChange={(v) => set("site_name_short", v)} testId="sc-site-name-short" placeholder="UGSHOOW" />
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Logo (replaces wordmark in header)</label>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-32 h-20 rounded-md border border-ink-600 bg-ink-900 flex items-center justify-center overflow-hidden">
                {content.logo_path ? (
                  <img src={fileUrl(content.logo_path)} alt="Site logo" className="max-w-full max-h-full object-contain" data-testid="sc-logo-preview" />
                ) : (
                  <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-[0.2em]">No logo</span>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" data-testid="sc-logo-input" />
              <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs disabled:opacity-60" data-testid="sc-logo-upload">
                <ImageSquare size={14} /> {uploadingLogo ? "Uploading…" : content.logo_path ? "Replace logo" : "Upload logo"}
              </button>
              {content.logo_path && (
                <button onClick={clearLogo} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-magenta hover:border-neon-magenta/50 text-xs" data-testid="sc-logo-clear">
                  <Trash size={14} /> Use wordmark
                </button>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2 font-mono">PNG / JPG / WEBP / SVG. Up to 25MB. Transparent PNG recommended.</p>
          </div>

          <div className="border-t border-ink-700 pt-4 space-y-3">
            <h4 className="text-[10px] font-bold tracking-[0.3em] text-amber-300 uppercase">Share template (owner only)</h4>
            <p className="text-xs text-zinc-500">
              Placeholders: <code className="text-neon-cyan font-mono">{`{song_title}`}</code>, <code className="text-neon-cyan font-mono">{`{artist_name}`}</code>, <code className="text-neon-cyan font-mono">{`{rank}`}</code>.
              The default line goes out with every share; per-platform lines below override it for that platform only.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Default slogan (all platforms)</label>
                <textarea
                  rows={2}
                  value={content.share_slogan_template || ""}
                  onChange={(e) => set("share_slogan_template", e.target.value)}
                  placeholder="{song_title} by {artist_name} is Number {rank} on the Undaground Shoow Top Ten"
                  className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60 font-mono"
                  data-testid="sc-share-slogan"
                />
              </div>
              <TextField label="Hashtags" value={content.share_hashtags} onChange={(v) => set("share_hashtags", v)} testId="sc-share-hashtags" placeholder="#UndagroundShoow #Top10" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">X / Twitter (short & punchy)</label>
                <textarea
                  rows={2}
                  value={content.share_slogan_x || ""}
                  onChange={(e) => set("share_slogan_x", e.target.value)}
                  placeholder="🔥 {song_title} — {artist_name} — #{rank} on The Undaground Shoow"
                  className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60 font-mono"
                  data-testid="sc-share-slogan-x"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Reddit (ask a question)</label>
                <textarea
                  rows={2}
                  value={content.share_slogan_reddit || ""}
                  onChange={(e) => set("share_slogan_reddit", e.target.value)}
                  placeholder="Is {song_title} by {artist_name} the best track at #{rank} on the Undaground Shoow this week?"
                  className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60 font-mono"
                  data-testid="sc-share-slogan-reddit"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Facebook</label>
                <textarea
                  rows={2}
                  value={content.share_slogan_facebook || ""}
                  onChange={(e) => set("share_slogan_facebook", e.target.value)}
                  placeholder="{song_title} by {artist_name} just hit #{rank} on the Undaground Shoow Top Ten. Give it a spin!"
                  className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60 font-mono"
                  data-testid="sc-share-slogan-facebook"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">WhatsApp (conversational)</label>
                <textarea
                  rows={2}
                  value={content.share_slogan_whatsapp || ""}
                  onChange={(e) => set("share_slogan_whatsapp", e.target.value)}
                  placeholder="Yo — check this out. {song_title} by {artist_name} at #{rank} on the Shoow 🔥"
                  className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60 font-mono"
                  data-testid="sc-share-slogan-whatsapp"
                />
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono">Leave any per-platform field blank to use the default slogan for that platform.</p>

            <div className="border-t border-ink-700/60 pt-3 mt-3">
              <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Share button color (mobile font + outline)</label>
              <p className="text-xs text-zinc-500 mb-2">The Share button on each song card uses this color for its text and outline. Pick a bright accent so it stays visible on both dark and light themes — especially on mobile.</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={content.share_button_color || "#00f0ff"}
                  onChange={(e) => set("share_button_color", e.target.value)}
                  className="w-10 h-10 rounded border border-ink-600 bg-transparent cursor-pointer"
                  data-testid="sc-share-button-color"
                />
                <input
                  value={content.share_button_color || ""}
                  onChange={(e) => set("share_button_color", e.target.value)}
                  placeholder="#00f0ff"
                  className="w-28 px-2 py-1 rounded bg-ink-900 border border-ink-600 text-white text-xs font-mono"
                />
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border-2 bg-ink-800/80 font-bold text-xs" style={{ color: content.share_button_color || "#00f0ff", borderColor: content.share_button_color || "#00f0ff" }}>
                  Preview: Share
                </span>
              </div>
            </div>
            {content.share_slogan_template && (
              <p className="text-xs text-zinc-400 font-mono" data-testid="sc-share-preview">
                Preview: <span className="text-zinc-200">
                  {(content.share_slogan_template || "")
                    .replace(/\{song_title\}/g, "Arch City Anthem")
                    .replace(/\{artist_name\}/g, "Almight E")
                    .replace(/\{rank\}/g, "1")}
                </span>
                {content.share_hashtags ? <span className="text-zinc-500"> {content.share_hashtags}</span> : null}
              </p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 space-y-4">
          <h3 className="text-sm font-bold tracking-tight text-white">Masthead & countdown</h3>
          <TextField label="Overline" value={content.hero_overline} onChange={(v) => set("hero_overline", v)} testId="sc-hero-overline" />
          <div className="grid grid-cols-2 gap-3">
            <TextField label='Title pt 1' value={content.hero_title_part1} onChange={(v) => set("hero_title_part1", v)} testId="sc-title1" />
            <TextField label='Title pt 2 (italic)' value={content.hero_title_part2} onChange={(v) => set("hero_title_part2", v)} testId="sc-title2" />
          </div>
          <TextField label="Title pt 3" value={content.hero_title_part3} onChange={(v) => set("hero_title_part3", v)} testId="sc-title3" />
          <TextField label="Title accent (gradient)" value={content.hero_title_accent} onChange={(v) => set("hero_title_accent", v)} testId="sc-title-accent" />
          <TextField label="Subtitle" value={content.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} multiline testId="sc-hero-subtitle" />
          <TextField label="Section label" value={content.section_label} onChange={(v) => set("section_label", v)} testId="sc-section-label" />
          <TextField label="Section heading" value={content.section_heading} onChange={(v) => set("section_heading", v)} testId="sc-section-heading" />
          <TextField label="Section meta (right-aligned)" value={content.section_meta} onChange={(v) => set("section_meta", v)} testId="sc-section-meta" />
        </div>

        <div className="space-y-5">
          <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 space-y-4">
            <h3 className="text-sm font-bold tracking-tight text-white inline-flex items-center gap-2"><Globe size={14} /> YouTube channel</h3>
            <TextField label="Channel URL (handle, /channel/UC..., or @handle)" value={content.youtube_channel_url} onChange={(v) => set("youtube_channel_url", v)} testId="sc-yt-url" placeholder="https://www.youtube.com/@theundagroundshoow" />
            <TextField label="Section title" value={content.youtube_section_title} onChange={(v) => set("youtube_section_title", v)} testId="sc-yt-title" />
            <p className="text-xs text-zinc-500">Once set, the public site pulls the latest 8 videos via free YouTube RSS — no API key.</p>
          </div>

          <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 space-y-4">
            <h3 className="text-sm font-bold tracking-tight text-white">Footer & contact</h3>
            <TextField label="Footer text" value={content.footer_text} onChange={(v) => set("footer_text", v)} testId="sc-footer" />
            <TextField label="Contact heading" value={content.contact_heading} onChange={(v) => set("contact_heading", v)} testId="sc-contact-heading" />
            <TextField label="Contact subheading" value={content.contact_subheading} onChange={(v) => set("contact_subheading", v)} testId="sc-contact-sub" />
          </div>

          <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5 space-y-4">
            <h3 className="text-sm font-bold tracking-tight text-white">Typography & colors</h3>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Heading font</label>
              <select value={content.heading_font} onChange={(e) => set("heading_font", e.target.value)} className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm" data-testid="sc-heading-font">
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-1.5">Body font</label>
              <select value={content.body_font} onChange={(e) => set("body_font", e.target.value)} className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm" data-testid="sc-body-font">
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase">Base font scale</label>
                <span className="text-xs font-mono text-neon-cyan">{content.base_font_scale.toFixed(2)}×</span>
              </div>
              <input type="range" min={0.7} max={1.6} step={0.05} value={content.base_font_scale} onChange={(e) => set("base_font_scale", Number(e.target.value))} className="progress w-full" data-testid="sc-font-scale" />
            </div>
            <ColorField label="Heading color" value={content.heading_color} onChange={(v) => set("heading_color", v)} testId="sc-heading-color" />
            <ColorField label="Body color" value={content.body_color} onChange={(v) => set("body_color", v)} testId="sc-body-color" />
            <ColorField label="Accent color" value={content.accent_color} onChange={(v) => set("accent_color", v)} testId="sc-accent-color" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-neon-cyan text-black font-semibold text-sm hover:bg-neon-cyanHover transition-colors disabled:opacity-60" data-testid="sc-save">
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving..." : "Save site content"}
        </button>
      </div>
    </section>
  );
}
